import { ROUTES } from '../lib/constants';
import { PRODUCT_NAME_TO_TRACK, PRODUCTS } from '../lib/constants/appConstants';
import { Package, PricingStepConfig, PricingQuestion } from '../interfaces/Types';
import { InsuranceHelper } from './InsuranceHelper';
import { SESSION_KEYS } from '../lib/constants/appConstants';

export class TrackingHelper {
  static getProductNumberToTrack = (product: string) => {
    switch (product) {
      case PRODUCTS.CAR:
        return '1001';
      case PRODUCTS.MC:
        return '1008';
      case PRODUCTS.HOME:
        return '2002';
      case PRODUCTS.VILLA:
        return '2005';
      case PRODUCTS.ACCIDENT:
        return '3008';
      case PRODUCTS.MOPED:
        return '1012';
      case PRODUCTS.PPI:
        return '4052';
    }
  };

  static getProductCategoryToTrack = (product: string) => {
    switch (product) {
      case PRODUCTS.CAR:
      case PRODUCTS.MC:
      case PRODUCTS.MOPED:
        return 'motor';
      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
        return 'building';
      case PRODUCTS.ACCIDENT:
        return 'accident-health';
      case PRODUCTS.PPI:
        return 'ppi';
    }
  };
  static getEventNameToTrackOnOfferLoad = (product: string) => {
    switch (product) {
      case PRODUCTS.CAR:
      case PRODUCTS.MC:
      case PRODUCTS.MOPED:
      case PRODUCTS.ACCIDENT:
      case PRODUCTS.PPI:
        return 'product_view';
      case PRODUCTS.HOME:
        return '';
    }
  };

  static getProductListToTrack = (productName: string) => {
    const selectedProductList = [];
    switch (productName) {
      case PRODUCTS.CAR:
        selectedProductList.push(PRODUCT_NAME_TO_TRACK.CAR);
        break;
      case PRODUCTS.MOPED:
        selectedProductList.push(PRODUCT_NAME_TO_TRACK.MOPED);
        break;
      case PRODUCTS.MC:
        selectedProductList.push(PRODUCT_NAME_TO_TRACK.MC);
        break;
      case PRODUCTS.HOME:
        selectedProductList.push(PRODUCT_NAME_TO_TRACK.HOME);
        break;
      case PRODUCTS.VILLA:
        selectedProductList.push(PRODUCT_NAME_TO_TRACK.VILLA);
        break;
      case PRODUCTS.ACCIDENT:
        selectedProductList.push(PRODUCT_NAME_TO_TRACK.ACCIDENT);
        break;
      case PRODUCTS.PPI:
        selectedProductList.push(PRODUCT_NAME_TO_TRACK.PPI); // PPI is tracked as 'bokvar' in the piwikpro
        break;
    }
    return selectedProductList;
  };

  static shouldTrackInsuranceSelections = (
    productName: string,
    nextPageUrl: string
  ) => {
    switch (productName) {
      case PRODUCTS.CAR:
      case PRODUCTS.MOPED:
      case PRODUCTS.MC:
      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
        return nextPageUrl.includes(ROUTES.summaryPage);
      case PRODUCTS.PPI:
      case PRODUCTS.ACCIDENT:
        return nextPageUrl.includes(ROUTES.paymentAppPath)
    }
    return false;
  };
  static getScenarioStepForInsuranceSelection = (productName: string) => {
    switch (productName) {
      case PRODUCTS.CAR:
      case PRODUCTS.MOPED:
      case PRODUCTS.MC:
      case PRODUCTS.ACCIDENT:
      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
      case PRODUCTS.PPI:
        return 'choose-plan';
    }
    return false;
  };

  static getProductVariant = (productName: string, selectedPackageNames: any[], pricingQuestions: PricingStepConfig[]) => {
    switch (productName) {
      case PRODUCTS.CAR:
      case PRODUCTS.MC:
      case PRODUCTS.MOPED:
      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
        return selectedPackageNames.join(';');
      case PRODUCTS.ACCIDENT:
        const stepQuestions = pricingQuestions.find((steps: PricingStepConfig) => steps.stepCode === '001')?.questions || [];
        let coverageAmount = '';
        if (stepQuestions.length > 0) {
          const coverageQuestion = stepQuestions.find((question: PricingQuestion) => question.APIKey === 'coverageAmount');
          if (coverageQuestion) {
            coverageAmount = InsuranceHelper.getSelectedLabel(coverageQuestion)
          }
        }
        return coverageAmount;
      case PRODUCTS.PPI:
        const stepPPIQuestions = pricingQuestions.find((steps: PricingStepConfig) => steps.stepCode === '001')?.questions || [];
        let coverageAmountPPI = '';
        if (stepPPIQuestions.length > 0) {
          const coverageQuestion = stepPPIQuestions.find((question: PricingQuestion) => question.APIKey === 'coverageAmount');
          if (coverageQuestion) {
            coverageAmount = coverageQuestion.selectedValue
          }
        }
        return coverageAmountPPI;
    }
    return '';
  };

  static getScenarioNameFromProduct = (productName: string) => {
    if (productName === PRODUCTS.VILLA) {
      return PRODUCT_NAME_TO_TRACK.VILLA;
    }
    if (productName === PRODUCTS.PPI) {
      return PRODUCT_NAME_TO_TRACK.PPI;
    }
    return productName;
  };

  static getScenarioStepFromPage = (page: string) => {
    switch (page) {
      case ROUTES.offerPage:
        return 'choose-plan'
      case ROUTES.summaryPage:
        return 'summary'
      case ROUTES.moreInputsPage:
        return 'details'
    }
    return '';
  }

  static getEcommerceInfoToTrack = (selectedPackages: Package[]) => {
    const selectedInsurancePackage = selectedPackages.find(
      (packageItem: Package) => !packageItem.isPlusPackage
    );
    const isPlusPacketSelected = selectedPackages.some(
      (packageItem: Package) => packageItem.isPlusPackage
    );

    let selectedPackageNames: (string | undefined)[] = [];
    let selectedPackageYearlyPrices: number[] = [];

    if (selectedInsurancePackage && selectedInsurancePackage.monthlyPrice) {
      selectedPackageYearlyPrices = [
        ...selectedPackageYearlyPrices,
        parseInt(selectedInsurancePackage.monthlyPrice) * 12
      ]
    }

    selectedPackageNames = [
      ...selectedPackageNames,
      selectedInsurancePackage?.packageName
    ];

    if (isPlusPacketSelected) {
      const plusPacketSelected = selectedPackages.filter(
        (packageItem: Package) => packageItem.isPlusPackage
      );
      plusPacketSelected?.forEach((plusPacket: Package) => {
        if (plusPacket.monthlyPrice) {
          selectedPackageYearlyPrices = [
            ...selectedPackageYearlyPrices,
            parseInt(plusPacket.monthlyPrice) * 12
          ];
        }
        if (plusPacket.packageName) {
          selectedPackageNames = [
            ...selectedPackageNames,
            plusPacket.packageHeaderLabel || plusPacket.packageName
          ];
        }
      });
    }
    const totalPrice = selectedPackageYearlyPrices.length > 0
      ? selectedPackageYearlyPrices?.reduce(
        (prevPrice: number, nextPrice: number) => prevPrice + nextPrice,
        0
      )
      : 0;

    return {
      selectedPackageNames,
      selectedPackageYearlyPrices,
      totalPrice
    };
  };

  static isFollowUpOffer = (policyNumber: string) => {
    const savedPolicy = sessionStorage.getItem(SESSION_KEYS.SAVED_POLICY);
    const followUpOffer = sessionStorage.getItem(SESSION_KEYS.FOLLOW_UP_OFFER);

    return policyNumber === savedPolicy || policyNumber === followUpOffer;
  };
}
